import { Button, Icon24ArrowRight, Typography } from "@cur8/maneki";
import { useClearRoomControllerMutation } from "hooks/mutations/useClearRoomControllerMutation";
import { usePatientQuery } from "hooks/queries/usePatientQuery";
import { useRoomQuery } from "hooks/queries/useRoomQuery";
import { useVisitHistoryQuery } from "hooks/queries/useVisitHistoryQuery";
import { useVisitQuery } from "hooks/queries/useVisitQuery";
import { formatTimeRange } from "lib/formatDateTime";
import { generateVisitTypeLabel } from "lib/generateVisitTypeLabel";
import { CheckInParams } from "lib/types/checkInParameters";
import { paths } from "render/routes/paths";
import { ActionsFooter } from "render/ui/layout/ActionsFooter";
import { InformationLayout } from "render/ui/layout/InformationLayout";
import { PageFrameContent } from "render/ui/layout/PageFrameContent";
import { AppointmentCard } from "render/ui/presentation/AppointmentCard";
import styles from "./styles.module.sass";

// The mirror of SelectVisitConfirm. Checkout used to take a single press with
// no confirmation and no way back, so an operator hit it mid-visit during US
// testing and a later scan could not be attributed to the member (AB#92438).
//
// Only the scan-progress screen reaches this, and it runs exactly what that
// button ran before: clear-room-controller, nothing else. Deliberately not
// complete-visit, which the backend rejects for base scans. See #88682.
export function CheckOutConfirmView({
  replaceWith,
  onBack,
  patientId,
  visitId,
}: {
  replaceWith(url: string): void;
  onBack(): void;
} & CheckInParams) {
  const { data: member } = usePatientQuery({ patientId });
  const { data: visit } = useVisitQuery({ patientId, visitId });
  const { data: room } = useRoomQuery({ roomId: visit?.roomId });
  const { data: visitHistory } = useVisitHistoryQuery({ patientId });

  // Filtered rather than interpolated: the sibling check-in screen renders
  // "undefined undefined" for the frame before the member query lands.
  const fullName = [member?.name?.firstName, member?.name?.lastName]
    .filter(Boolean)
    .join(" ");
  const countryCode = visit?.site.address.countryCode?.toUpperCase();
  const time =
    visit != null
      ? formatTimeRange(visit.startTime, visit.endTime, countryCode)
      : undefined;
  const visitTypeLabel =
    visit != null && visitHistory != null
      ? generateVisitTypeLabel({ visit, visitHistory })
      : undefined;

  // Reads mid-sentence, so the fallback stays lowercase.
  const firstName =
    member?.name?.firstName ?? member?.name?.displayName ?? "the member";

  const clearRoomController = useClearRoomControllerMutation();

  return (
    <PageFrameContent>
      <InformationLayout
        content={
          <section className={styles.layout}>
            <div className={styles.title}>
              <Typography variant="display-s">Confirm check-out</Typography>
              <Typography variant="body-m" color="subtle">
                This unassigns the room from {firstName}&apos;s visit. The room
                becomes available for the next member.
              </Typography>
            </div>

            <AppointmentCard
              topLeft={fullName}
              topRight={room?.displayName}
              bottomLeft={visitTypeLabel}
              bottomRight={time}
            />
          </section>
        }
        buttons={
          <ActionsFooter
            left={
              <Button variant="secondary" onPress={onBack} fillContainer>
                Back
              </Button>
            }
            right={
              <Button
                onPress={() => {
                  clearRoomController.mutate(
                    { patientId, visitId },
                    {
                      // Replaces rather than pushes: once the room is
                      // unassigned this screen has nothing left to do, and a
                      // back-stack entry would let the operator return and
                      // press Check out a second time.
                      onSuccess: () =>
                        replaceWith(
                          paths.checkOutConfirmed.url({ patientId, visitId })
                        ),
                    }
                  );
                }}
                fillContainer
                icon={<Icon24ArrowRight />}
                state={clearRoomController.isPending ? "loading" : undefined}
              >
                Check out
              </Button>
            }
          />
        }
      />
    </PageFrameContent>
  );
}
